import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import "./WordGameDnD2.css";

const ItemType = {
  WORD: "dnd2-word",
};

const clampBucketCount = (value) => Math.min(4, Math.max(1, Number(value) || 2));
const normalizeBucketSize = (value) => ([1, 2, 3].includes(Number(value)) ? Number(value) : 2);
const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const buildBuckets = (buckets, bucketCount) =>
  Array.from({ length: bucketCount }, (_, idx) => {
    const source = normalizeArray(buckets)[idx] || {};
    const id = String(source.id || `bucket${idx + 1}`);

    return {
      id,
      title: String(source.title ?? source.label ?? ""),
      backgroundImage: String(source.backgroundImage || source.backgroundImageUrl || ""),
    };
  });

const Word = ({ id, text, isDraggable, draggedWords, disableWhenPlaced = false, onDraggingChange }) => {
  const isAlreadyDragged = disableWhenPlaced && draggedWords.includes(id);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemType.WORD,
    item: { id, text },
    canDrag: isDraggable && !isAlreadyDragged,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    onDraggingChange?.(id, isDragging);
  }, [id, isDragging, onDraggingChange]);

  return (
    <div
      ref={isDraggable && !isAlreadyDragged ? drag : null}
      className={`dnd2-word${isAlreadyDragged ? " is-used" : ""}${isDragging ? " is-dragging" : ""}`}
      style={{
        cursor: isDraggable && !isAlreadyDragged ? "move" : "default",
        pointerEvents: isDraggable && !isAlreadyDragged ? "auto" : "none",
      }}
    >
      {text}
    </div>
  );
};

const WordSource = ({ isDraggingWord, onDropBack, children }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemType.WORD,
    drop: (item) => onDropBack(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [onDropBack]);

  return (
    <div ref={drop} className={`dnd2-word-container${isDraggingWord ? " is-available" : ""}${isOver ? " is-over" : ""}`}>
      {children}
    </div>
  );
};

const DropZone = ({ bucket, bucketSize, isDraggingWord, onDrop, children }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemType.WORD,
    drop: (item) => onDrop(item, bucket.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [bucket.id, onDrop]);

  const backgroundImage = bucket.backgroundImage
    ? `url("${String(bucket.backgroundImage).replace(/"/g, '\\"')}")`
    : undefined;

  return (
    <div
      className={`dnd2-drop-container size-${bucketSize}${bucket.backgroundImage ? " has-background" : ""}${isDraggingWord ? " is-available" : ""}${isOver ? " is-over" : ""}`}
      ref={drop}
      style={{
        backgroundImage,
      }}
    >
      {children}
    </div>
  );
};

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const WordGameDnD2 = ({
  onScoreUpdate,
  prevScore,
  onGameOver,
  question,
  imgPath,
  videoPath,
  initialWords,
  buckets,
  bucketCount,
  bucketSize,
}) => {
  const safeWords = useMemo(() => normalizeArray(initialWords), [initialWords]);
  const visibleBucketCount = clampBucketCount(bucketCount ?? buckets?.length ?? 2);
  const visibleBuckets = useMemo(
    () => buildBuckets(buckets, visibleBucketCount),
    [buckets, visibleBucketCount]
  );
  const safeBucketSize = normalizeBucketSize(bucketSize);
  const validBucketIds = useMemo(
    () => new Set(visibleBuckets.map((bucket) => bucket.id)),
    [visibleBuckets]
  );
  const fallbackBucketId = visibleBuckets[0]?.id || "bucket1";

  const normalizedWords = useMemo(
    () =>
      safeWords.map((word, idx) => ({
        id: word?.id ?? idx + 1,
        text: String(word?.text || ""),
        type: validBucketIds.has(word?.type) ? word.type : fallbackBucketId,
      })),
    [safeWords, validBucketIds, fallbackBucketId]
  );

  const [bucketWords, setBucketWords] = useState({});
  const [newScore, setNewScore] = useState(prevScore);
  const [draggedWords, setDraggedWords] = useState([]);
  const [activeDraggedId, setActiveDraggedId] = useState(null);
  const prevScoreRef = useRef(prevScore);
  const baseScoreRef = useRef(prevScore);

  useEffect(() => {
    prevScoreRef.current = prevScore;
  }, [prevScore]);

  useEffect(() => {
    baseScoreRef.current = prevScoreRef.current;
    setBucketWords({});
    setNewScore(prevScoreRef.current);
    setDraggedWords([]);
    setActiveDraggedId(null);
  }, [normalizedWords]);

  useEffect(() => {
    if (normalizedWords.length > 0 && draggedWords.length === normalizedWords.length) {
      onGameOver(true);
    } else {
      onGameOver(false);
    }
  }, [draggedWords.length, normalizedWords.length, onGameOver]);

  useEffect(() => {
    if (newScore !== prevScore) {
      onScoreUpdate(newScore);
    }
  }, [newScore, onScoreUpdate, prevScore]);

  const recalculateScore = useCallback((nextBucketWords) => {
    const placedWords = Object.entries(nextBucketWords).flatMap(([bucketId, words]) =>
      normalizeArray(words).map((word) => ({ ...word, bucketId }))
    );
    const delta = placedWords.reduce((sum, placedWord) => {
      const word = normalizedWords.find((candidate) => candidate.id === placedWord.id);
      return sum + (word?.type === placedWord.bucketId ? 0.5 : -0.5);
    }, 0);

    setNewScore(baseScoreRef.current + delta);
  }, [normalizedWords]);

  useEffect(() => {
    recalculateScore(bucketWords);
  }, [bucketWords, recalculateScore]);

  const handleDrop = (item, targetBucketId) => {
    setBucketWords((prev) => ({
      ...Object.fromEntries(
        Object.entries(prev).map(([bucketId, words]) => [
          bucketId,
          normalizeArray(words).filter((word) => word.id !== item.id),
        ])
      ),
      [targetBucketId]: [
        ...normalizeArray(prev[targetBucketId]).filter((word) => word.id !== item.id),
        item,
      ],
    }));
    setDraggedWords((prev) => [...prev.filter((id) => id !== item.id), item.id]);
  };

  const handleDropBack = (item) => {
    setBucketWords((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([bucketId, words]) => [
          bucketId,
          normalizeArray(words).filter((word) => word.id !== item.id),
        ])
      )
    );
    setDraggedWords((prev) => prev.filter((id) => id !== item.id));
  };

  const handleDraggingChange = useCallback((id, isDragging) => {
    setActiveDraggedId((currentId) => {
      if (isDragging) return id;
      return currentId === id ? null : currentId;
    });
  }, []);

  const isDraggingWord = activeDraggedId != null;

  return (
    <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
      <div className="module-game-dnd2">
        {String(question || "").trim() && (
          <div
            className="dnd2-instructions question-title"
            dangerouslySetInnerHTML={{ __html: question }}
          ></div>
        )}

        {imgPath && (
          <img style={{ maxWidth: "100%" }} src={imgPath} alt="moduleImg" />
        )}

        {videoPath && (
          <iframe
            width="350px"
            height="250px"
            style={{ maxWidth: "100%" }}
            src={videoPath}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )}

        <WordSource isDraggingWord={isDraggingWord} onDropBack={handleDropBack}>
          {normalizedWords.map((word) => (
            <div className="dnd2-word-row" key={word.id}>
              <Word
                id={word.id}
                text={word.text}
                isDraggable={true}
                draggedWords={draggedWords}
                disableWhenPlaced={true}
                onDraggingChange={handleDraggingChange}
              />
            </div>
          ))}
        </WordSource>

        <div
          className={`dnd2-drop-zones count-${visibleBucketCount}`}
          style={{ "--dnd2-count": visibleBucketCount }}
        >
          {visibleBuckets.map((bucket) => (
            <DropZone
              key={bucket.id}
              bucket={bucket}
              bucketSize={safeBucketSize}
              isDraggingWord={isDraggingWord}
              onDrop={handleDrop}
            >
              {bucket.title.trim() && (
                <div
                  className="dnd2-zone-title"
                  dangerouslySetInnerHTML={{ __html: bucket.title }}
                ></div>
              )}
              <div className="dnd2-bucket-words">
                {normalizeArray(bucketWords[bucket.id]).map((word) => (
                  <Word
                    key={word.id}
                    id={word.id}
                    text={word.text}
                    isDraggable={true}
                    draggedWords={draggedWords}
                    onDraggingChange={handleDraggingChange}
                  />
                ))}
              </div>
            </DropZone>
          ))}
        </div>
      </div>
    </DndProvider>
  );
};

export default WordGameDnD2;
